import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "filter-bar" };
const _hoisted_2 = { class: "filter-row-secondary" };
const _hoisted_3 = {
    key: 0,
    class: "loading-state"
};
const _hoisted_4 = {
    key: 0,
    class: "drill-crumb"
};
const _hoisted_5 = { class: "crumb-current" };
const _hoisted_6 = { class: "summary-grid" };
const _hoisted_7 = {
    key: 1,
    class: "charts-row"
};
const _hoisted_8 = { class: "chart-card" };
const _hoisted_9 = { class: "chart-card" };
const _hoisted_10 = {
    key: 6,
    class: "inline-panel"
};
const _hoisted_11 = { class: "inline-panel-header" };
const _hoisted_12 = { class: "inline-panel-actions" };
const _hoisted_13 = {
    key: 1,
    class: "inline-panel-footer"
};
const _hoisted_14 = {
    key: 7,
    class: "inline-panel"
};
const _hoisted_15 = { class: "inline-panel-header" };
const _hoisted_16 = {
    key: 8,
    class: "inline-panel"
};
const _hoisted_17 = { class: "inline-panel-header" };
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance, inject, useTemplateRef } from 'vue';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useContent } from '@/composables/useContent';
import { useTransactions } from '@/composables/useTransactions';
import { usePolling } from '@/composables/usePolling';
import { getSpendingAnalysis, getTransactionClassificationTree, clearSpendingAnalysisCache, buildSpendingOverviewBreakdown, buildCategoryRows, buildTransactionGroups, groupTransactionsByDay, formatTimestampString, findCurrentMonthIndex, getMonthRangeForSelection, GRANDPARENT_CLASSIFICATIONS, SPENDING_BREAKDOWN_PALETTE, } from '@/composables/useSpendingAnalysis';
import { selectedFilter, selectedPeriod, selectedAccount, selectedSubCategory, selectedSubCategoryColour, selectedMerchant, dateSelection, snapToSpendingCycle, } from '@/components/elements/spendingAnalysisFilterState';
import { useSpendingPlansSidebar } from '@/composables/useSpendingPlansSidebar';
import SpendingFilterTabs from '@/components/elements/SpendingFilterTabs.vue';
import PeriodSelector from '@/components/elements/PeriodSelector.vue';
import SpendingSummaryCard from '@/components/elements/SpendingSummaryCard.vue';
import CategoryAnalysisList from '@/components/elements/CategoryAnalysisList.vue';
import RecentTransactionsSection from '@/components/elements/RecentTransactionsSection.vue';
import UncategorisedCard from '@/components/elements/UncategorisedCard.vue';
import InfoPanel from '@/components/elements/InfoPanel.vue';
import TransactionsList from '@/components/elements/TransactionsList.vue';
import { currentTaxYearLabel, formatMonthYearShort, formatResponseRange } from '@/utils/helpers';
import HorizontalSpendingChart from '../components/HorizontalSpendingChart.vue';
import SpendingDonutChart from '../components/SpendingDonutChart.vue';
import TopMerchantsChart from '../components/TopMerchantsChart.vue';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import AccountSelectorDesktop from '../components/AccountSelectorDesktop.vue';
import DateRangeDialog from '../components/DateRangeDialog.vue';
import ChangeCategoryDialog from '../components/ChangeCategoryDialog.vue';
// ---------- Constants ----------
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingAnalysisPage',
    emits: ["colour-mode-changed"],
    setup(__props, { emit: __emit }) {
        const PERIODS = [
            { key: 'month', label: 'Month' },
            { key: 'Year', label: 'Year' },
            { key: 'tax', label: currentTaxYearLabel() },
        ];
        // ---------- Composables ----------
        const state = useAppState();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const { getCustomer, savePreferences } = useCustomer();
        const { getBankingInfo, getRecurringPayments } = useBanking();
        const { getPlans } = useContent();
        const transactionsApi = useTransactions();
        const { poll } = usePolling('spendingAnalysisBulkUpdate');
        const registerSidebarFn = inject('registerSidebar', null);
        const { fetchAndRegisterSpendingPlans, registerSpendingPlansSidebar } = useSpendingPlansSidebar(registerSidebarFn);
        const categoryDialog = useTemplateRef('categoryDialog');
        const singleTxnDialog = useTemplateRef('singleTxnDialog');
        const selectedTxn = ref(null);
        const emit = __emit;
        // ---------- Local UI state ----------
        const colourMode = ref('light');
        const ready = ref(false);
        const showSettings = ref(false);
        const fetchReady = ref(false);
        const summaryLoading = ref(false);
        const summary = ref(null);
        const filterTabs = ref([{ key: 'all', label: 'All' }]);
        const periods = ref(PERIODS);
        const recurringPayments = ref(null);
        const dateDialogOpen = ref(false);
        const uncategorisedDrawerOpen = ref(false);
        const transactionsDrawerOpen = ref(false);
        const analysisDrawerOpen = ref(false);
        const analysisDrawerTab = ref('analysis');
        const uncategorisedSelectMode = ref(false);
        const uncategorisedSelectedIds = ref(new Set());
        const customer = computed(() => state.customer);
        const formatter = computed(() => state.formatter);
        const safeTop = computed(() => parseInt(cookies?.get('st') ?? 0));
        // ---------- Filter / view computeds ----------
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        const isAllView = computed(() => selectedFilter.value === 'all' && !selectedSubCategory.value && !selectedMerchant.value);
        const selectedTab = computed(() => filterTabs.value.find(t => t.key === selectedFilter.value));
        const selectedCategoryName = computed(() => (selectedTab.value && selectedTab.value.key !== 'all') ? selectedTab.value.label : undefined);
        const selectedFilterLabel = computed(() => selectedMerchant.value || selectedSubCategory.value || selectedTab.value?.label || '');
        const selectedFilterColour = computed(() => {
            if (selectedSubCategory.value) {
                return selectedSubCategoryColour.value || SPENDING_BREAKDOWN_PALETTE[0];
            }
            if (selectedTab.value?.color)
                return selectedTab.value.color;
            const parent = (summary.value?.parentCategories ?? []).find((p) => p.name === selectedFilterLabel.value);
            const txColour = parent?.categoryColor ?? parent?.transactions?.[0]?.categoryColor ?? parent?.color ?? parent?.transactions?.[0]?.color;
            return txColour ?? SPENDING_BREAKDOWN_PALETTE[0];
        });
        // ---------- Range / fetch ----------
        const monthRange = computed(() => getMonthRangeForSelection(dateSelection.value, selectedPeriod.value));
        const analysisRange = computed(() => {
            if (monthRange.value) {
                const [fy, fm] = monthRange.value.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.value.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            const now = new Date();
            let year = now.getUTCFullYear();
            let month = now.getUTCMonth();
            const cycleDay = state.bankInfo?.spendingCycle?.day;
            if (snapToSpendingCycle.value && cycleDay && now.getUTCDate() >= cycleDay) {
                month += 1;
                if (month > 11) {
                    month = 0;
                    year += 1;
                }
            }
            return {
                fromDate: new Date(Date.UTC(year, month, 1)),
                toDate: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59)),
            };
        });
        function fetchSummary() {
            const { fromDate, toDate } = analysisRange.value;
            const opts = {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            };
            if (selectedMerchant.value)
                opts.merchant = selectedMerchant.value;
            else if (selectedSubCategory.value)
                opts.subCategory = selectedSubCategory.value;
            else if (selectedCategoryName.value)
                opts.category = selectedCategoryName.value;
            summaryLoading.value = true;
            getSpendingAnalysis(fromDate, toDate, opts, (result) => {
                if (result)
                    summary.value = result;
                summaryLoading.value = false;
            });
        }
        // ---------- Data shaping ----------
        const hasSummary = computed(() => !!summary.value && Array.isArray(summary.value.parentCategories) && summary.value.parentCategories.length > 0);
        const rangeLabel = computed(() => {
            const r = summary.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            const sel = dateSelection.value;
            if (sel) {
                if (sel.type === 'months' && sel.startMonth) {
                    const start = formatMonthYearShort(sel.startMonth);
                    if (!sel.endMonth || sel.endMonth === sel.startMonth)
                        return start;
                    return `${start} - ${formatMonthYearShort(sel.endMonth)}`;
                }
                return sel.label ?? '';
            }
            if (selectedPeriod.value === 'month')
                return 'This month';
            if (selectedPeriod.value === 'Year')
                return 'This year';
            if (selectedPeriod.value === 'tax')
                return 'This tax year';
            return '';
        });
        const spendingSummary = computed(() => {
            const r = summary.value;
            if (!r || !isAllView.value) {
                return { rangeLabel: rangeLabel.value, net: 0, totalIn: 0, totalOut: 0, breakdown: [] };
            }
            const breakdown = buildSpendingOverviewBreakdown(r.parentCategories, (name) => filterTabs.value.find(t => t.label === name)?.color ?? null);
            return {
                rangeLabel: rangeLabel.value,
                net: r.net ?? ((r.totalIn ?? 0) - (r.totalOut ?? 0)),
                totalIn: r.totalIn ?? 0,
                totalOut: r.totalOut ?? 0,
                breakdown,
            };
        });
        const categorySliceBars = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            if (!Array.isArray(slices))
                return [];
            const now = Date.now();
            return slices.map((s) => {
                const from = s.fromDate ? new Date(s.fromDate).getTime() : 0;
                return {
                    label: s.label,
                    value: Math.abs(s.totalOut ?? 0) || Math.abs(s.net ?? 0),
                    isFuture: from > now,
                    fromDate: s.fromDate,
                    toDate: s.toDate,
                };
            });
        });
        const dataIsWeekly = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            return Array.isArray(slices) && slices.length > 0 && slices.every((s) => s.week === true);
        });
        const currentMonthIndex = computed(() => findCurrentMonthIndex(categorySliceBars.value));
        const currentCategories = computed(() => buildCategoryRows(summary.value, isAllView.value, selectedSubCategory.value, selectedSubCategoryColour.value));
        const currentMerchants = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const byName = new Map();
            for (const p of parents) {
                const merchants = Array.isArray(p.spendByMerchant) ? p.spendByMerchant : [];
                for (const m of merchants) {
                    const totalIn = m.totalIn ?? 0;
                    const totalOut = m.totalOut ?? 0;
                    const amount = Math.abs(totalOut > totalIn ? totalOut : totalIn);
                    if (amount <= 0)
                        continue;
                    const wellKnown = Array.isArray(m.transactions) && m.transactions.some((t) => t.wellKnownMerchant);
                    const iconPath = m.transactions?.[0]?.icon ?? p.transactions?.[0]?.icon;
                    const iconColour = m.transactions?.[0]?.color ?? p.transactions?.[0]?.color ?? p.color;
                    const existing = byName.get(m.merchantName);
                    if (existing) {
                        existing.amount += amount;
                        existing.transactionCount += m.transactionCount ?? 0;
                        if (wellKnown)
                            existing.merchant = true;
                        if (!existing.iconPath && iconPath)
                            existing.iconPath = iconPath;
                        if (!existing.iconColour && iconColour)
                            existing.iconColour = iconColour;
                    }
                    else {
                        byName.set(m.merchantName, {
                            name: m.merchantName,
                            transactionCount: m.transactionCount ?? 0,
                            amount,
                            merchant: wellKnown ? true : undefined,
                            iconPath,
                            iconColour,
                        });
                    }
                }
            }
            return [...byName.values()].sort((a, b) => b.amount - a.amount);
        });
        const apiTransactionGroups = computed(() => buildTransactionGroups(summary.value));
        function isUncategorised(t) {
            return !t.subClassification || t.subClassification === 'Uncategorised' || t.subClassification === 'Uncategorized';
        }
        const uncategorisedTransactionGroups = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const flat = [];
            const seen = new Set();
            for (const p of parents) {
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (!isUncategorised(t))
                        continue;
                    const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    flat.push({
                        id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                        timestampString: formatTimestampString(t.timestamp),
                        wellKnownMerchant: t.wellKnownMerchant,
                        effectiveClassification: {
                            mgClassification: t.classification,
                            mgSubClassification: t.subClassification,
                            color: p.color ?? t.color,
                            iconPath: t.icon,
                        },
                    });
                }
            }
            return groupTransactionsByDay(flat);
        });
        const uncategorisedCount = computed(() => uncategorisedTransactionGroups.value.reduce((sum, g) => sum + g.length, 0));
        // ---------- Charts adapters ----------
        const categoryTotals = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            return parents.map((p, i) => {
                const net = p.net ?? ((p.totalIn ?? 0) - (p.totalOut ?? 0));
                const isIncome = (p.totalIn ?? 0) > (p.totalOut ?? 0) && net >= 0;
                const fallback = SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length];
                const colour = filterTabs.value.find(t => t.label === p.name)?.color ?? p.categoryColor ?? p.color ?? fallback;
                return {
                    id: isIncome ? GRANDPARENT_CLASSIFICATIONS.INCOME : (i + 1),
                    name: p.name,
                    colour,
                    value: Math.abs(net),
                };
            }).filter((c) => c.value > 0);
        });
        const topMerchants = computed(() => currentMerchants.value.slice(0, 8).map(m => ({ name: m.name, value: m.amount })));
        // ---------- Event handlers ----------
        function openAnalysisInline(tab) {
            analysisDrawerTab.value = tab;
            analysisDrawerOpen.value = true;
        }
        function openTransactionsInline() {
            transactionsDrawerOpen.value = true;
        }
        function openUncategorisedInline() {
            uncategorisedDrawerOpen.value = true;
        }
        function onDateRangeSelect(selection) {
            dateSelection.value = selection;
        }
        function onClearDateSelection() {
            dateSelection.value = null;
            selectedPeriod.value = 'month';
        }
        function onMonthBarClick(bar) {
            if (!bar.fromDate)
                return;
            const d = new Date(bar.fromDate);
            if (Number.isNaN(d.getTime()))
                return;
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const monthKey = `${y}-${m}`;
            dateSelection.value = {
                type: 'months',
                startMonth: monthKey,
                endMonth: monthKey,
                label: formatMonthYearShort(monthKey),
            };
            selectedPeriod.value = 'month';
        }
        function onCategoryClick(name) {
            const tab = filterTabs.value.find(t => t.label === name);
            if (tab)
                selectedFilter.value = tab.key;
        }
        function handleDonutCategoryClick(categoryId) {
            const item = categoryTotals.value.find((c) => c.id === categoryId);
            if (item)
                onCategoryClick(item.name);
        }
        function onSubCategoryClick(name, colour) {
            selectedSubCategory.value = name;
            selectedSubCategoryColour.value = colour || '';
        }
        function onMerchantClick(name) {
            selectedMerchant.value = name;
        }
        async function onDrawerCategoryClick(name, colour) {
            onSubCategoryClick(name, colour);
            analysisDrawerOpen.value = false;
            await nextTick();
            window.scrollTo({ top: 0 });
        }
        async function onDrawerMerchantClick(name) {
            onMerchantClick(name);
            analysisDrawerOpen.value = false;
            await nextTick();
            window.scrollTo({ top: 0 });
        }
        function goToTransaction(t) {
            // Desktop: don't leak into the mobile TransactionDetailPage. Instead open the
            // recategorise dialog inline — that's the main reason a user clicks a row.
            selectedTxn.value = t;
            nextTick(() => singleTxnDialog.value?.openDrawer());
        }
        function onSingleCategoryApply(payload) {
            const t = selectedTxn.value;
            if (!payload.classification || !t?.id) {
                singleTxnDialog.value?.closeDrawer();
                return;
            }
            transactionsApi.applyCategoryToTransactions([t], payload, () => {
                clearSpendingAnalysisCache();
                singleTxnDialog.value?.closeDrawer();
                fetchSummary();
            });
        }
        function pollUntilBulkApplied(updatedIds) {
            let latestSummary = null;
            const { fromDate, toDate } = analysisRange.value;
            const opts = {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            };
            poll({
                remaining: 5,
                interval: 1000,
                fetch: (cb) => {
                    getSpendingAnalysis(fromDate, toDate, opts, (result) => {
                        latestSummary = result;
                        cb();
                    });
                },
                onPoll: () => { },
                shouldStop: () => {
                    if (!latestSummary)
                        return false;
                    const parents = Array.isArray(latestSummary.parentCategories) ? latestSummary.parentCategories : [];
                    for (const p of parents) {
                        const txs = Array.isArray(p.transactions) ? p.transactions : [];
                        for (const t of txs) {
                            if (updatedIds.has(t.id) && (!t.subClassification || t.subClassification === 'Uncategorised' || t.subClassification === 'Uncategorized')) {
                                return false;
                            }
                        }
                    }
                    return true;
                },
                onFinish: () => {
                    if (latestSummary)
                        summary.value = latestSummary;
                    categoryDialog.value?.closeDrawer();
                    uncategorisedDrawerOpen.value = false;
                },
            });
        }
        function openMarkSelectedDialog() {
            categoryDialog.value?.openDrawer();
        }
        function onBulkCategoryApply(payload) {
            if (!payload.classification) {
                categoryDialog.value?.closeDrawer();
                return;
            }
            const flatSelected = uncategorisedTransactionGroups.value
                .flat()
                .filter((t) => uncategorisedSelectedIds.value.has(t.id));
            if (!flatSelected.length) {
                categoryDialog.value?.closeDrawer();
                return;
            }
            const updatedIds = new Set(flatSelected.map((t) => t.id));
            transactionsApi.applyCategoryToTransactions(flatSelected, payload, () => {
                clearSpendingAnalysisCache();
                pollUntilBulkApplied(updatedIds);
            });
        }
        function toggleUncategorisedSelectMode() {
            uncategorisedSelectMode.value = !uncategorisedSelectMode.value;
            if (!uncategorisedSelectMode.value)
                uncategorisedSelectedIds.value = new Set();
        }
        function onUncategorisedRowClick(t) {
            if (!uncategorisedSelectMode.value) {
                goToTransaction(t);
                return;
            }
            const next = new Set(uncategorisedSelectedIds.value);
            if (next.has(t.id))
                next.delete(t.id);
            else
                next.add(t.id);
            uncategorisedSelectedIds.value = next;
        }
        function onBack() {
            if (selectedMerchant.value) {
                selectedMerchant.value = '';
            }
            else if (selectedSubCategory.value) {
                selectedSubCategory.value = '';
            }
            else if (selectedFilter.value !== 'all') {
                selectedFilter.value = 'all';
            }
            window.scrollTo({ top: 0 });
        }
        function selectColourMode(mode) {
            colourMode.value = mode;
            localStorage.setItem('mg_colour_mode', mode);
            emit('colour-mode-changed', mode);
        }
        function updatePref(key, value) {
            if (customer.value?.preferences) {
                customer.value.preferences[key] = value;
                savePreferences();
            }
        }
        // ---------- Lifecycle ----------
        onMounted(() => {
            const savedMode = localStorage.getItem('mg_colour_mode');
            colourMode.value = savedMode || 'light';
            getPlans();
            getRecurringPayments((res) => { recurringPayments.value = res; });
            getCustomer(() => {
                getBankingInfo(() => {
                    getTransactionClassificationTree((tree) => {
                        if (tree?.parents) {
                            const apiTabs = tree.parents.map((p) => ({
                                key: p.parent?.key ?? p.name,
                                label: p.name,
                                categoryId: p.parent?.id,
                                color: p.categoryColor ?? p.parent?.categoryColor ?? p.color ?? p.parent?.color,
                            }));
                            const order = ['Income', 'Essentials', 'Lifestyle', 'Future', 'General'];
                            apiTabs.sort((a, b) => {
                                const ai = order.indexOf(a.label);
                                const bi = order.indexOf(b.label);
                                return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                            });
                            filterTabs.value = [{ key: 'all', label: 'All' }, ...apiTabs];
                        }
                        fetchReady.value = true;
                        ready.value = true;
                        fetchSummary();
                        registerSpendingPlansSidebar({
                            onOpenSettings: () => { showSettings.value = true; }
                        });
                        fetchAndRegisterSpendingPlans(null, {
                            onOpenSettings: () => { showSettings.value = true; }
                        });
                    });
                });
            });
        });
        watch([analysisRange, selectedAccountIds, snapToSpendingCycle, selectedFilter, selectedSubCategory, selectedMerchant], () => {
            if (!fetchReady.value)
                return;
            fetchSummary();
        });
        watch([selectedFilter, selectedSubCategory, selectedMerchant], () => {
            window.scrollTo({ top: 0 });
        });
        watch([uncategorisedCount, summaryLoading], ([count, loading]) => {
            if (loading)
                return;
            if (summary.value === null)
                return;
            if (uncategorisedDrawerOpen.value && count === 0) {
                uncategorisedDrawerOpen.value = false;
            }
        });
        watch(uncategorisedDrawerOpen, (open) => {
            if (!open) {
                uncategorisedSelectMode.value = false;
                uncategorisedSelectedIds.value = new Set();
            }
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["desktop-analysis-page", colourMode.value])
            }, [
                (showSettings.value)
                    ? (_openBlock(), _createBlock(SettingsOverlay, {
                        key: 0,
                        customer: customer.value,
                        colourMode: colourMode.value,
                        safeTop: safeTop.value,
                        onClose: _cache[0] || (_cache[0] = ($event) => (showSettings.value = false)),
                        onUpdatePref: updatePref,
                        onSelectColourMode: selectColourMode
                    }, null, 8, ["customer", "colourMode", "safeTop"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("div", _hoisted_1, [
                            (!_unref(selectedSubCategory) && !_unref(selectedMerchant))
                                ? (_openBlock(), _createBlock(SpendingFilterTabs, {
                                    key: 0,
                                    modelValue: _unref(selectedFilter),
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_isRef(selectedFilter) ? (selectedFilter).value = $event : null)),
                                    tabs: filterTabs.value
                                }, null, 8, ["modelValue", "tabs"]))
                                : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_2, [
                                _createVNode(PeriodSelector, {
                                    modelValue: _unref(selectedPeriod),
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (_isRef(selectedPeriod) ? (selectedPeriod).value = $event : null)),
                                    periods: periods.value,
                                    dateSelection: _unref(dateSelection),
                                    onOpenDrawer: _cache[3] || (_cache[3] = ($event) => (dateDialogOpen.value = true)),
                                    onClearSelection: onClearDateSelection
                                }, null, 8, ["modelValue", "periods", "dateSelection"])
                            ]),
                            _createVNode(AccountSelectorDesktop, {
                                modelValue: _unref(selectedAccount),
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (_isRef(selectedAccount) ? (selectedAccount).value = $event : null))
                            }, null, 8, ["modelValue"])
                        ]),
                        (!ready.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "120px",
                                    class: "mb-4"
                                }),
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "300px",
                                    class: "mb-4"
                                })
                            ]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                (_unref(selectedSubCategory) || _unref(selectedMerchant))
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        _createElementVNode("button", {
                                            class: "crumb-back",
                                            onClick: onBack
                                        }, [
                                            _createVNode(_component_v_icon, { size: "16" }, {
                                                default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                    _createTextVNode("mdi-arrow-left")
                                                ])),
                                                _: 1
                                            }),
                                            _cache[10] || (_cache[10] = _createElementVNode("span", null, "Back", -1))
                                        ]),
                                        _createElementVNode("span", _hoisted_5, _toDisplayString(selectedFilterLabel.value), 1)
                                    ]))
                                    : _createCommentVNode("", true),
                                _createElementVNode("div", _hoisted_6, [
                                    (isAllView.value)
                                        ? (_openBlock(), _createBlock(HorizontalSpendingChart, {
                                            key: "overview",
                                            data: spendingSummary.value,
                                            onCategoryClick: onCategoryClick
                                        }, null, 8, ["data"]))
                                        : (_openBlock(), _createBlock(SpendingSummaryCard, {
                                            key: 'category-' + (_unref(selectedMerchant) || _unref(selectedSubCategory) || _unref(selectedFilter)),
                                            mode: "category",
                                            categoryLabel: selectedFilterLabel.value,
                                            monthlyData: categorySliceBars.value,
                                            weeklyData: dataIsWeekly.value ? categorySliceBars.value : undefined,
                                            currentMonthIndex: currentMonthIndex.value,
                                            barColour: selectedFilterColour.value,
                                            onMonthClick: onMonthBarClick
                                        }, null, 8, ["categoryLabel", "monthlyData", "weeklyData", "currentMonthIndex", "barColour"]))
                                ]),
                                (hasSummary.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                        _createElementVNode("div", _hoisted_8, [
                                            _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "chart-title" }, "Category Breakdown", -1)),
                                            _createVNode(SpendingDonutChart, {
                                                categoryTotals: categoryTotals.value,
                                                formatter: formatter.value,
                                                colourMode: colourMode.value,
                                                onCategoryClick: handleDonutCategoryClick
                                            }, null, 8, ["categoryTotals", "formatter", "colourMode"])
                                        ]),
                                        _createElementVNode("div", _hoisted_9, [
                                            _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "chart-title" }, "Top Merchants", -1)),
                                            _createVNode(TopMerchantsChart, {
                                                merchants: topMerchants.value,
                                                formatter: formatter.value,
                                                colourMode: colourMode.value,
                                                onMerchantClick: onMerchantClick
                                            }, null, 8, ["merchants", "formatter", "colourMode"])
                                        ])
                                    ]))
                                    : _createCommentVNode("", true),
                                (!_unref(selectedMerchant) && (currentCategories.value.length > 0 || currentMerchants.value.length > 0))
                                    ? (_openBlock(), _createBlock(CategoryAnalysisList, {
                                        key: _unref(selectedSubCategory) || _unref(selectedFilter) || 'all',
                                        categories: currentCategories.value,
                                        merchants: currentMerchants.value,
                                        showMerchants: !_unref(selectedMerchant),
                                        initialTab: _unref(selectedSubCategory) ? 'merchants' : 'analysis',
                                        dateRange: rangeLabel.value,
                                        onViewMore: openAnalysisInline,
                                        onCategoryClick: onSubCategoryClick,
                                        onMerchantClick: onMerchantClick
                                    }, null, 8, ["categories", "merchants", "showMerchants", "initialTab", "dateRange"]))
                                    : _createCommentVNode("", true),
                                (apiTransactionGroups.value.length > 0)
                                    ? (_openBlock(), _createBlock(RecentTransactionsSection, {
                                        key: 3,
                                        transactions: apiTransactionGroups.value,
                                        formatter: formatter.value,
                                        today: _unref(state).today,
                                        yesterday: _unref(state).yesterday,
                                        accountType: "all",
                                        onTransactionClick: goToTransaction,
                                        categoryPills: true,
                                        onViewMore: openTransactionsInline,
                                        limit: 3
                                    }, null, 8, ["transactions", "formatter", "today", "yesterday"]))
                                    : _createCommentVNode("", true),
                                (!summaryLoading.value && uncategorisedCount.value > 0)
                                    ? (_openBlock(), _createBlock(UncategorisedCard, {
                                        key: 4,
                                        count: uncategorisedCount.value,
                                        class: "uncat-card",
                                        onClick: openUncategorisedInline
                                    }, null, 8, ["count"]))
                                    : _createCommentVNode("", true),
                                (recurringPayments.value && recurringPayments.value.payments.length > 0)
                                    ? (_openBlock(), _createBlock(InfoPanel, {
                                        key: 5,
                                        href: "/#/RecurringPayments",
                                        valueHref: "/#/RecurringPayments",
                                        icon: require('@/assets/coins.png'),
                                        title: "Recurring Payments",
                                        subtitle: "Stay on top of your payments",
                                        valueTitle: "Count",
                                        value: recurringPayments.value.payments.length
                                    }, null, 8, ["icon", "value"]))
                                    : _createCommentVNode("", true),
                                (uncategorisedDrawerOpen.value)
                                    ? (_openBlock(), _createElementBlock("section", _hoisted_10, [
                                        _createElementVNode("div", _hoisted_11, [
                                            _createElementVNode("h3", null, "Uncategorised — " + _toDisplayString(uncategorisedCount.value) + " transaction" + _toDisplayString(uncategorisedCount.value === 1 ? '' : 's'), 1),
                                            _createElementVNode("div", _hoisted_12, [
                                                _createElementVNode("button", {
                                                    class: "link-btn",
                                                    onClick: toggleUncategorisedSelectMode
                                                }, _toDisplayString(uncategorisedSelectMode.value ? 'Cancel' : 'Select multiple'), 1),
                                                _createElementVNode("button", {
                                                    class: "link-btn",
                                                    onClick: _cache[5] || (_cache[5] = ($event) => (uncategorisedDrawerOpen.value = false))
                                                }, "Close")
                                            ])
                                        ]),
                                        (uncategorisedTransactionGroups.value.length > 0)
                                            ? (_openBlock(), _createBlock(TransactionsList, {
                                                key: 0,
                                                showDateTotal: false,
                                                transactions: uncategorisedTransactionGroups.value,
                                                formatter: formatter.value,
                                                today: _unref(state).today,
                                                yesterday: _unref(state).yesterday,
                                                dividingLines: true,
                                                onTransactionClick: onUncategorisedRowClick,
                                                selectable: uncategorisedSelectMode.value,
                                                selectedIds: uncategorisedSelectedIds.value
                                            }, null, 8, ["transactions", "formatter", "today", "yesterday", "selectable", "selectedIds"]))
                                            : _createCommentVNode("", true),
                                        (uncategorisedSelectedIds.value.size > 0)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                                _createElementVNode("button", {
                                                    class: "primary-btn",
                                                    onClick: openMarkSelectedDialog
                                                }, " Mark " + _toDisplayString(uncategorisedSelectedIds.value.size) + " selected as... ", 1)
                                            ]))
                                            : _createCommentVNode("", true)
                                    ]))
                                    : _createCommentVNode("", true),
                                (transactionsDrawerOpen.value)
                                    ? (_openBlock(), _createElementBlock("section", _hoisted_14, [
                                        _createElementVNode("div", _hoisted_15, [
                                            _cache[13] || (_cache[13] = _createElementVNode("h3", null, "All transactions", -1)),
                                            _createElementVNode("button", {
                                                class: "link-btn",
                                                onClick: _cache[6] || (_cache[6] = ($event) => (transactionsDrawerOpen.value = false))
                                            }, "Close")
                                        ]),
                                        (apiTransactionGroups.value.length > 0)
                                            ? (_openBlock(), _createBlock(TransactionsList, {
                                                key: 0,
                                                search: true,
                                                showDateTotal: false,
                                                transactions: apiTransactionGroups.value,
                                                formatter: formatter.value,
                                                today: _unref(state).today,
                                                yesterday: _unref(state).yesterday,
                                                dividingLines: true,
                                                onTransactionClick: goToTransaction,
                                                categoryPills: true
                                            }, null, 8, ["transactions", "formatter", "today", "yesterday"]))
                                            : _createCommentVNode("", true)
                                    ]))
                                    : _createCommentVNode("", true),
                                (analysisDrawerOpen.value)
                                    ? (_openBlock(), _createElementBlock("section", _hoisted_16, [
                                        _createElementVNode("div", _hoisted_17, [
                                            _createElementVNode("h3", null, _toDisplayString(analysisDrawerTab.value === 'merchants' ? 'Merchants' : 'Categories'), 1),
                                            _createElementVNode("button", {
                                                class: "link-btn",
                                                onClick: _cache[7] || (_cache[7] = ($event) => (analysisDrawerOpen.value = false))
                                            }, "Close")
                                        ]),
                                        (_openBlock(), _createBlock(CategoryAnalysisList, {
                                            key: analysisDrawerTab.value,
                                            categories: currentCategories.value,
                                            merchants: currentMerchants.value,
                                            showMerchants: !_unref(selectedMerchant),
                                            limit: 0,
                                            initialTab: analysisDrawerTab.value,
                                            dateRange: rangeLabel.value,
                                            onCategoryClick: onDrawerCategoryClick,
                                            onMerchantClick: onDrawerMerchantClick
                                        }, null, 8, ["categories", "merchants", "showMerchants", "initialTab", "dateRange"]))
                                    ]))
                                    : _createCommentVNode("", true)
                            ], 64))
                    ], 64)),
                _createVNode(DateRangeDialog, {
                    modelValue: dateDialogOpen.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((dateDialogOpen).value = $event)),
                    initialSelection: _unref(dateSelection),
                    onSelect: onDateRangeSelect
                }, null, 8, ["modelValue", "initialSelection"]),
                _createVNode(ChangeCategoryDialog, {
                    ref_key: "categoryDialog",
                    ref: categoryDialog,
                    onApply: onBulkCategoryApply
                }, null, 512),
                _createVNode(ChangeCategoryDialog, {
                    ref_key: "singleTxnDialog",
                    ref: singleTxnDialog,
                    merchantName: selectedTxn.value?.merchantName,
                    currentClassification: selectedTxn.value?.effectiveClassification,
                    currentExcludeFromAnalysis: selectedTxn.value?.excludeFromAnalysis ?? false,
                    onApply: onSingleCategoryApply
                }, null, 8, ["merchantName", "currentClassification", "currentExcludeFromAnalysis"])
            ], 2));
        };
    }
});
